// @ts-nocheck
import { useState, useMemo, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { 
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from './ui/pagination';
import { FilterConfig } from './filter-popover';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  ChevronDown,
  MoreHorizontal,
  Archive,
  Trash2,
  Tag,
  X,
  Download,
  RefreshCw,
  Columns3,
  Filter,
  Search
} from 'lucide-react';

// Base interfaces
export interface BaseItem {
  id: string | number;
  [key: string]: any;
}

export interface ColumnDef<T = any> {
  key: string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  filterConfig?: FilterConfig[];
  render?: (item: T) => React.ReactNode;
  width?: string;
  className?: string;
  /** If false, column is hidden by default and can be shown via the column visibility picker. Default true. */
  defaultVisible?: boolean;
}

export interface BulkAction {
  key: string;
  label: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'destructive' | 'outline';
  requireConfirmation?: boolean;
  confirmTitle?: string;
  confirmDescription?: string;
}

export interface AdvancedDataTableV2Props<T extends BaseItem> {
  title: string;
  description?: string;
  data: T[];
  columns: ColumnDef<T>[];
  bulkActions?: BulkAction[];
  pageSize?: number;
  onRefresh?: () => void;
  onExport?: () => void;
  onBulkAction?: (action: string, selectedIds: (string | number)[]) => void;
  onRowClick?: (item: T) => void;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  emptyStateIcon?: React.ReactNode;
  /** Exclude these values by default when no filter is selected. E.g. { status: ['Archived', 'Merged'] } */
  defaultExclude?: Record<string, string[]>;
}

type SortDirection = 'asc' | 'desc';

export function AdvancedDataTableV2<T extends BaseItem>({
  title,
  description,
  data,
  columns,
  bulkActions = [],
  pageSize = 10,
  onRefresh,
  onExport,
  onBulkAction,
  onRowClick,
  emptyStateTitle = "No data found",
  emptyStateDescription = "Try adjusting your search or filters",
  emptyStateIcon,
  defaultExclude
}: AdvancedDataTableV2Props<T>) {
  // Column visibility: default = show columns where defaultVisible !== false
  const allColumnKeys = useMemo(() => columns.map(c => c.key), [columns]);
  const defaultVisibleKeys = useMemo(
    () => columns.filter(c => c.defaultVisible !== false).map(c => c.key),
    [columns]
  );
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<Set<string>>(() => 
    new Set(defaultVisibleKeys.length > 0 ? defaultVisibleKeys : allColumnKeys)
  );
  const prevColumnKeysStrRef = useRef<string>('');
  useEffect(() => {
    const keysStr = columns.map(c => c.key).join(',');
    if (prevColumnKeysStrRef.current === keysStr) return;
    prevColumnKeysStrRef.current = keysStr;
    const defaults = columns.filter(c => c.defaultVisible !== false).map(c => c.key);
    const keys = defaults.length > 0 ? defaults : columns.map(c => c.key);
    setVisibleColumnKeys(new Set(keys));
  }, [columns]);
  const visibleColumns = useMemo(
    () => columns.filter(c => visibleColumnKeys.has(c.key)),
    [columns, visibleColumnKeys]
  );

  const setColumnVisible = (key: string, visible: boolean) => {
    setVisibleColumnKeys(prev => {
      const next = new Set(prev);
      if (visible) next.add(key);
      else {
        if (next.size <= 1) return prev;
        next.delete(key);
      }
      return next;
    });
  };

  // State management
  const [selectedItems, setSelectedItems] = useState<(string | number)[]>([]);
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const [filterValues, setFilterValues] = useState<Record<string, Record<string, string | string[]>>>({});
  const [dateRangeValues, setDateRangeValues] = useState<Record<string, { from: string; to: string }>>({});
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const hasFilterableColumns = useMemo(
    () => columns.some(c => c.filterable && c.filterConfig),
    [columns]
  );

  // Filtering and sorting logic
  const filteredAndSortedData = useMemo(() => {
    let filtered = data.filter(item => {
      // Default exclude: hide these values when no filter explicitly selects them
      if (defaultExclude) {
        for (const [excludeKey, excludeValues] of Object.entries(defaultExclude)) {
          if (excludeValues.length === 0) continue;
          const col = columns.find(c => c.filterConfig?.some(f => f.key === excludeKey));
          if (!col) continue;
          const filterSelected = filterValues[col.key]?.[excludeKey];
          const selected = Array.isArray(filterSelected) ? filterSelected : (filterSelected && filterSelected !== 'all' ? [filterSelected] : []);
          if (selected.length === 0) {
            const itemVal = String(item[excludeKey] ?? '');
            if (excludeValues.some(v => itemVal.toLowerCase() === String(v).toLowerCase())) return false;
          }
        }
      }

      // Apply search filters per column
      for (const [columnKey, query] of Object.entries(searchQueries)) {
        if (query) {
          const queryLower = query.toLowerCase();
          const value = item[columnKey];
          let matchesSearch = false;
          
          if (Array.isArray(value)) {
            matchesSearch = value.some(v => String(v).toLowerCase().includes(queryLower));
          } else {
            matchesSearch = String(value || '').toLowerCase().includes(queryLower);
          }
          
          if (!matchesSearch) return false;
        }
      }

      // Apply custom filters per column (multi-select: item matches if in selected set)
      for (const [columnKey, columnFilters] of Object.entries(filterValues)) {
        for (const [filterKey, filterValue] of Object.entries(columnFilters)) {
          const selected = Array.isArray(filterValue) ? filterValue : (filterValue && filterValue !== 'all' ? [filterValue] : []);
          if (selected.length > 0) {
            const itemVal = String(item[filterKey] ?? '');
            const itemNorm = itemVal.toLowerCase().replace(/\s+/g, ' ');
            const matches = selected.some(v => 
              itemNorm === String(v).toLowerCase().replace(/\s+/g, ' ') ||
              itemNorm === String(v).toLowerCase().replace(/\s+/g, '-')
            );
            if (!matches) return false;
          }
        }
      }

      // Apply date range filters
      for (const [columnKey, range] of Object.entries(dateRangeValues)) {
        if (range.from || range.to) {
          const itemValue = item[columnKey];
          if (!itemValue) return false;
          const itemDate = new Date(itemValue);
          if (isNaN(itemDate.getTime())) return false;
          if (range.from) {
            const fromDate = new Date(range.from);
            fromDate.setHours(0, 0, 0, 0);
            if (itemDate < fromDate) return false;
          }
          if (range.to) {
            const toDate = new Date(range.to);
            toDate.setHours(23, 59, 59, 999);
            if (itemDate > toDate) return false;
          }
        }
      }

      return true;
    });

    // Sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        // Handle different data types
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [data, searchQueries, filterValues, dateRangeValues, sortField, sortDirection, defaultExclude, columns]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredAndSortedData.slice(startIndex, endIndex);

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(paginatedData.map(item => item.id));
      setShowBulkActions(true);
    } else {
      setSelectedItems([]);
      setShowBulkActions(false);
    }
  };

  const handleSelectItem = (itemId: string | number, checked: boolean) => {
    if (checked) {
      const newSelected = [...selectedItems, itemId];
      setSelectedItems(newSelected);
      setShowBulkActions(newSelected.length > 0);
    } else {
      const newSelected = selectedItems.filter(id => id !== itemId);
      setSelectedItems(newSelected);
      setShowBulkActions(newSelected.length > 0);
    }
  };

  // Sorting handler
  const handleSort = (field: string) => {
    const column = columns.find(col => col.key === field);
    if (!column?.sortable) return;

    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter handlers
  const handleSearchChange = (columnKey: string, value: string) => {
    setSearchQueries(prev => ({ ...prev, [columnKey]: value }));
    setCurrentPage(1);
  };

  const handleFilterChange = (columnKey: string, filterKey: string, value: string) => {
    setFilterValues(prev => ({
      ...prev,
      [columnKey]: {
        ...prev[columnKey],
        [filterKey]: value
      }
    }));
    setCurrentPage(1);
  };

  const handleMultiSelectToggle = (columnKey: string, filterKey: string, optionValue: string) => {
    setFilterValues(prev => {
      const current = prev[columnKey]?.[filterKey];
      const arr = Array.isArray(current) ? current : (current && current !== 'all' ? [current] : []);
      const next = arr.includes(optionValue)
        ? arr.filter(v => v !== optionValue)
        : [...arr, optionValue];
      return {
        ...prev,
        [columnKey]: {
          ...prev[columnKey],
          [filterKey]: next
        }
      };
    });
    setCurrentPage(1);
  };

  const getSelectedForFilter = (columnKey: string, filterKey: string): string[] => {
    const v = filterValues[columnKey]?.[filterKey];
    return Array.isArray(v) ? v : (v && v !== 'all' ? [v] : []);
  };

  const handleDateRangeChange = (columnKey: string, field: 'from' | 'to', value: string) => {
    setDateRangeValues(prev => ({
      ...prev,
      [columnKey]: {
        ...prev[columnKey],
        [field]: value
      }
    }));
    setCurrentPage(1);
  };

  const handleClearFilters = (columnKey: string) => {
    const col = columns.find(c => c.key === columnKey);
    if (col?.filterConfig) {
      const searchKeys = col.filterConfig.filter(f => f.type === 'search').map(f => f.key);
      if (searchKeys.length > 0) {
        setSearchQueries(prev => {
          const next = { ...prev };
          searchKeys.forEach(k => { next[k] = ''; });
          return next;
        });
      }
    }
    setFilterValues(prev => ({ ...prev, [columnKey]: {} }));
    setDateRangeValues(prev => ({ ...prev, [columnKey]: { from: '', to: '' } }));
    setCurrentPage(1);
  };

  const handleApplyFilters = () => {
    // Filters are applied in real-time via the inline filter row
  };

  const isSelectFilterActive = (v: string | string[] | undefined) => {
    if (!v) return false;
    if (Array.isArray(v)) return v.length > 0;
    return v !== 'all';
  };

  // Check if column has active filters
  const hasActiveFilters = (columnKey: string) => {
    const col = columns.find(c => c.key === columnKey);
    const searchKeys = col?.filterConfig?.filter(f => f.type === 'search').map(f => f.key) || [columnKey];
    const hasSearch = searchKeys.some(k => searchQueries[k]);
    const hasFilters = filterValues[columnKey] && 
      Object.values(filterValues[columnKey]).some(isSelectFilterActive);
    const hasDateRange = dateRangeValues[columnKey] && 
      (dateRangeValues[columnKey].from || dateRangeValues[columnKey].to);
    return !!(hasSearch || hasFilters || hasDateRange);
  };

  // Bulk actions
  const handleBulkActionClick = (action: string) => {
    if (onBulkAction) {
      onBulkAction(action, selectedItems);
    }
    setSelectedItems([]);
    setShowBulkActions(false);
  };

  // Check if any filters are active
  const countSelectFilters = (col: Record<string, string | string[]>) => 
    Object.values(col).reduce((n, v) => n + (Array.isArray(v) ? v.length : (v && v !== 'all' ? 1 : 0)), 0);
  const hasAnyActiveFilters = Object.values(searchQueries).some(query => query) || 
    Object.values(filterValues).some(col => countSelectFilters(col) > 0) ||
    Object.values(dateRangeValues).some(range => range.from || range.to);

  // Pagination helpers
  const generatePaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (startPage > 1) {
      items.push(
        <PaginationItem key="start">
          <PaginationLink onClick={() => setCurrentPage(1)}>1</PaginationLink>
        </PaginationItem>
      );
      if (startPage > 2) {
        items.push(<PaginationEllipsis key="start-ellipsis" />);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink 
            onClick={() => setCurrentPage(i)}
            isActive={currentPage === i}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<PaginationEllipsis key="end-ellipsis" />);
      }
      items.push(
        <PaginationItem key="end">
          <PaginationLink onClick={() => setCurrentPage(totalPages)}>{totalPages}</PaginationLink>
        </PaginationItem>
      );
    }

    return items;
  };

  return (
    <div className="space-y-4 min-w-0">
      <Card className="min-w-0">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{title}</CardTitle>
              {description && (
                <CardDescription>
                  {description} - {filteredAndSortedData.length} of {data.length} items
                </CardDescription>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {hasFilterableColumns && (
                <Button
                  variant={showFilters ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  {hasAnyActiveFilters && (
                    <Badge variant="secondary" className="ml-1.5 h-5 min-w-[20px] px-1.5 text-xs">
                      {Object.values(searchQueries).filter(q => q).length + 
                       Object.values(filterValues).reduce((acc, col) => acc + countSelectFilters(col), 0) +
                       Object.values(dateRangeValues).filter(r => r.from || r.to).length}
                    </Badge>
                  )}
                </Button>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Columns3 className="h-4 w-4 mr-2" />
                    Columns
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="end">
                  <div className="text-sm font-medium mb-2">Toggle columns</div>
                  <div className="space-y-1 max-h-[280px] overflow-y-auto">
                    {columns.map((col) => (
                      <label
                        key={col.key}
                        className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted cursor-pointer"
                      >
                        <Checkbox
                          checked={visibleColumnKeys.has(col.key)}
                          onCheckedChange={(checked) => setColumnVisible(col.key, !!checked)}
                          disabled={visibleColumnKeys.has(col.key) && visibleColumnKeys.size === 1}
                        />
                        <span className="truncate">{col.label || col.key}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              {onRefresh && (
                <Button variant="outline" size="sm" onClick={onRefresh}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              )}
              {onExport && (
                <Button variant="outline" size="sm" onClick={onExport}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Active filters indicator */}
          {hasAnyActiveFilters && (
            <div className="flex items-center justify-between p-2.5 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">Filters applied</span>
                <Badge variant="secondary">
                  {Object.values(searchQueries).filter(q => q).length + 
                   Object.values(filterValues).reduce((acc, col) => acc + countSelectFilters(col), 0) +
                   Object.values(dateRangeValues).filter(r => r.from || r.to).length} active
                </Badge>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setSearchQueries({});
                  setFilterValues({});
                  setDateRangeValues({});
                  setCurrentPage(1);
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Clear all filters
              </Button>
            </div>
          )}

          {/* Bulk Actions Bar */}
          {showBulkActions && bulkActions.length > 0 && (
            <div className="flex items-center justify-between p-2.5 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">{selectedItems.length} items selected</span>
              </div>
              <div className="flex items-center space-x-2">
                {bulkActions.map((action) => (
                  action.requireConfirmation ? (
                    <AlertDialog key={action.key}>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant={action.variant || "outline"} 
                          size="sm"
                          className=""
                        >
                          {action.icon}
                          {action.label}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {action.confirmTitle || `Confirm ${action.label}`}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {action.confirmDescription || `Are you sure you want to ${action.label.toLowerCase()} ${selectedItems.length} items? This action cannot be undone.`}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            className={action.variant === 'destructive' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
                            onClick={() => handleBulkActionClick(action.key)}
                          >
                            {action.label}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  ) : (
                    <Button 
                      key={action.key}
                      variant={action.variant || "outline"} 
                      size="sm"
                      onClick={() => handleBulkActionClick(action.key)}
                    >
                      {action.icon}
                      {action.label}
                    </Button>
                  )
                ))}
              </div>
            </div>
          )}

          {/* Table - overflow-x-auto so wide tables scroll horizontally within viewport */}
          <div className="border rounded-lg overflow-x-auto">
            <Table className="min-w-full">
              <TableHeader>
                <TableRow>
                  {bulkActions.length > 0 && (
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedItems.length === paginatedData.length && paginatedData.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                  )}
                  {visibleColumns.map((column) => (
                    <TableHead 
                      key={column.key}
                      className={`${column.width || ''} ${column.className || ''}`}
                    >
                      <div className="flex items-center space-x-2">
                        <span>{column.label}</span>
                        {column.sortable && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                            onClick={() => handleSort(column.key)}
                          >
                            {sortField === column.key && sortDirection === 'asc' && (
                              <ArrowUp className="h-3.5 w-3.5" />
                            )}
                            {sortField === column.key && sortDirection === 'desc' && (
                              <ArrowDown className="h-3.5 w-3.5" />
                            )}
                            {sortField !== column.key && (
                              <ArrowUpDown className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        )}
                        {column.filterable && hasActiveFilters(column.key) && (
                          <Badge variant="secondary" className="h-4 w-4 rounded-full p-0 text-[10px] flex items-center justify-center bg-primary text-primary-foreground">
                            !
                          </Badge>
                        )}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
                {/* Inline filter row */}
                {showFilters && (
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    {bulkActions.length > 0 && <TableHead className="w-12" />}
                    {visibleColumns.map((column) => (
                      <TableHead key={`filter-${column.key}`} className="py-2 px-3">
                        {column.filterable && column.filterConfig ? (
                          <div className="space-y-1.5">
                            {column.filterConfig.map((filter) => {
                              if (filter.type === 'search') {
                                return (
                                  <div key={filter.key} className="relative">
                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                    <Input
                                      placeholder={filter.placeholder || `Filter ${filter.label.toLowerCase()}...`}
                                      value={searchQueries[filter.key] || ''}
                                      onChange={(e) => handleSearchChange(filter.key, e.target.value)}
                                      className="h-8 pl-7 text-xs bg-background"
                                    />
                                  </div>
                                );
                              }
                              if (filter.type === 'select' && filter.options) {
                                const selected = getSelectedForFilter(column.key, filter.key);
                                return (
                                  <Popover key={filter.key}>
                                    <PopoverTrigger asChild>
                                      <Button variant="outline" className="h-8 text-xs bg-background justify-between font-normal w-full min-w-[100px]">
                                        <span className="truncate">
                                          {selected.length === 0
                                            ? `All ${filter.label}`
                                            : selected.length === 1
                                              ? filter.options.find(o => o.value === selected[0])?.label ?? selected[0]
                                              : `${selected.length} selected`}
                                        </span>
                                        <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[200px] p-2" align="start">
                                      <div className="max-h-[240px] overflow-y-auto space-y-1">
                                        {filter.options.map((option) => (
                                          <label
                                            key={option.value}
                                            className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted cursor-pointer text-sm"
                                          >
                                            <Checkbox
                                              checked={selected.includes(option.value)}
                                              onCheckedChange={() => handleMultiSelectToggle(column.key, filter.key, option.value)}
                                            />
                                            <span className="truncate">{option.label}</span>
                                          </label>
                                        ))}
                                      </div>
                                      {selected.length > 0 && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="w-full mt-2 text-xs"
                                          onClick={() => {
                                            setFilterValues(prev => ({
                                              ...prev,
                                              [column.key]: { ...prev[column.key], [filter.key]: [] }
                                            }));
                                            setCurrentPage(1);
                                          }}
                                        >
                                          Clear selection
                                        </Button>
                                      )}
                                    </PopoverContent>
                                  </Popover>
                                );
                              }
                              if (filter.type === 'dateRange') {
                                return (
                                  <div key={filter.key} className="flex flex-col gap-1">
                                    <Input
                                      type="date"
                                      value={dateRangeValues[column.key]?.from || ''}
                                      onChange={(e) => handleDateRangeChange(column.key, 'from', e.target.value)}
                                      className="h-8 text-xs bg-background"
                                      placeholder="From"
                                      title="From date"
                                    />
                                    <Input
                                      type="date"
                                      value={dateRangeValues[column.key]?.to || ''}
                                      onChange={(e) => handleDateRangeChange(column.key, 'to', e.target.value)}
                                      className="h-8 text-xs bg-background"
                                      placeholder="To"
                                      title="To date"
                                    />
                                  </div>
                                );
                              }
                              return null;
                            })}
                          </div>
                        ) : (
                          <span />
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                )}
              </TableHeader>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + (bulkActions.length > 0 ? 1 : 0)} className="py-6 text-muted-foreground">
                      <div className="flex flex-col items-center justify-center gap-2 text-center">
                        {emptyStateIcon && <div className="opacity-50">{emptyStateIcon}</div>}
                        <div className="font-medium">{emptyStateTitle}</div>
                        <div className="text-sm">{emptyStateDescription}</div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((item) => (
                    <TableRow 
                      key={item.id} 
                      className={`hover:bg-muted/50 ${onRowClick ? 'cursor-pointer' : ''}`}
                      onClick={(e) => {
                        // Don't trigger row click if clicking on checkbox or action buttons
                        const target = e.target as HTMLElement;
                        if (onRowClick && !target.closest('button') && !target.closest('[role="checkbox"]')) {
                          onRowClick(item);
                        }
                      }}
                    >
                      {bulkActions.length > 0 && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedItems.includes(item.id)}
                            onCheckedChange={(checked) => handleSelectItem(item.id, checked as boolean)}
                          />
                        </TableCell>
                      )}
                      {visibleColumns.map((column) => (
                        <TableCell key={column.key} className={column.className}>
                          {column.render ? column.render(item) : String(item[column.key] || '')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredAndSortedData.length)} of {filteredAndSortedData.length} results
              </div>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  {generatePaginationItems()}
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}

          {/* Footer with selection info */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div>
              {filteredAndSortedData.length !== data.length && `Filtered: ${filteredAndSortedData.length} of ${data.length} items`}
            </div>
            <div className="flex items-center space-x-2">
              {selectedItems.length > 0 && (
                <span>{selectedItems.length} selected</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}